import {
  type ColumnSizingState,
  getCoreRowModel,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { ChevronDown, Link01, Stars01, Tag01, Trash02 } from '@timelyapp/icons/line-icons';
import { Button, DropdownMenu, Modal, Table } from '@timelyapp/react';
import type { AgentSession, AssignProjectBody } from 'api/agentSessions/agentSessions.api';
import classNames from 'classnames';
import TagFilterDropdown from 'components/dropdowns/TagFilterDropdown';
import { TimesheetProposalLinkedWorkSelect } from 'domains/ai/timesheet_proposals/components/TimesheetProposal.LinkedWorkSelect';
import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { getTags, makeGetTagSearchOptionsForProjects } from 'redux/modules/tags.selectors';
import { getHasLinkedWorkPicker } from 'redux/modules/tic/tic.integrations.selectors';
import { getUserId } from 'redux/selectors/user';
import AgentProjectPicker, { assignErrorMessage } from './AgentProjectPicker';
import {
  AgentSelectionContext,
  type AgentSelectionValue,
  AgentTableContext as Context,
  useAgentTableWorkspace,
} from './AgentTableWorkspace.context';
import styles from './AgentTableWorkspace.module.scss';
import { agentTableResize, agentTableSizing, isFlexibleAgentColumn } from './agentTableSizing';
import {
  bulkPredictionFetchers,
  bulkPredictionTargets,
  useBulkProjectPredictions,
} from './bulkProjectPredictions';
import { ProjectSuggestionReceipts } from './SessionProjectSuggestions';

export { useAgentTableWorkspace } from './AgentTableWorkspace.context';

import {
  type AgentPeriod,
  agentTagAssignmentUpdates,
  tagsColumnWidth,
} from './agentTracking.model';

export function useAgentTagOptions(periods: AgentPeriod[], projectOverride?: number | null) {
  const projectKey = JSON.stringify(
    [
      ...new Set(periods.map(p => (projectOverride === undefined ? p.projectId : projectOverride))),
    ].sort((a, b) => (a ?? 0) - (b ?? 0))
  );
  const selector = useMemo(
    () => makeGetTagSearchOptionsForProjects(JSON.parse(projectKey)),
    [projectKey]
  );
  return useSelector(selector);
}

const COLUMNS = [
  { id: 'provider', header: 'Provider', size: 40, minSize: 40, maxSize: 40, enableResizing: false },
  { id: 'title', header: 'Session', size: 320, minSize: 210, maxSize: Number.MAX_SAFE_INTEGER },
  { id: 'project', header: 'Project', size: 240, minSize: 160 },
  { id: 'linked', header: 'Linked work', size: 230, minSize: 120 },
  // Start with avatars only; widening reveals first names.
  {
    id: 'conductor',
    header: 'Conductor',
    size: 48,
    minSize: 48,
    maxSize: 320,
  },
  { id: 'tags', header: 'Tags', size: 180, minSize: 100 },
  { id: 'seconds', header: 'Agent time', size: 100, minSize: 100 },
  { id: 'tokens', header: 'Tokens', size: 90, minSize: 80 },
  { id: 'cost', header: 'Est. cost', size: 100, minSize: 100 },
];
const EMPTY_DATA: unknown[] = [];
const STORAGE_KEY = 'agentsheet-table-columns-v1';
function readPreferences(): { sizing: ColumnSizingState; visibility: VisibilityState } {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      sizing: Object.fromEntries(
        COLUMNS.filter(c => Number.isFinite(saved.sizing?.[c.id])).map(c => [
          c.id,
          Math.max(
            c.minSize,
            Math.min(
              c.maxSize ?? (isFlexibleAgentColumn(c.id) ? Infinity : 800),
              saved.sizing[c.id]
            )
          ),
        ])
      ),
      visibility: Object.fromEntries(
        COLUMNS.filter(
          c =>
            c.id !== 'title' && c.id !== 'provider' && typeof saved.visibility?.[c.id] === 'boolean'
        ).map(c => [c.id, saved.visibility[c.id]])
      ),
    };
  } catch {
    return { sizing: {}, visibility: {} };
  }
}
/** Resolves with the session as the server saved it. */
export type AssignReview = (sessionId: number, patch: AssignProjectBody) => Promise<AgentSession>;
export type PublishSession = (sessionId: number, published: boolean) => Promise<AgentSession>;
export const publishErrorMessage = (published: boolean) =>
  published
    ? 'Could not publish the session. Try again.'
    : 'Could not make the session private. Try again.';
function useWorkspace(
  periods: AgentPeriod[],
  onAssign?: AssignReview,
  onDelete?: (sessionId: number) => Promise<void>,
  onPublish?: PublishSession
) {
  const [preferences] = useState(readPreferences);
  const [sizing, setSizing] = useState(preferences.sizing);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  useLayoutEffect(() => {
    if (!container) return;
    const measure = () => setAvailableWidth(container.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [container]);
  const [visibility, setVisibility] = useState(preferences.visibility);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [patch, setPatch] = useState<AssignProjectBody>({});
  const tags = useAgentTagOptions(
    periods.filter(p => selected.has(p.segmentId)),
    patch.project_id
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pendingDeletion, setPendingDeletion] = useState<AgentPeriod[]>([]);
  const [deletionError, setDeletionError] = useState('');
  const requestDelete = onDelete
    ? (rows: AgentPeriod[]) => {
        if (saving) return;
        setDeletionError('');
        setPendingDeletion([...new Map(rows.map(p => [p.sessionId, p])).values()]);
      }
    : undefined;
  function cancelDelete() {
    if (!saving) setPendingDeletion([]);
  }
  async function confirmDelete() {
    if (!onDelete || saving || !pendingDeletion.length) return;
    setSaving(true);
    setDeletionError('');
    const failed: AgentPeriod[] = [];
    let firstError = '';
    for (const session of pendingDeletion) {
      try {
        await onDelete(session.sessionId);
        const removedIds = new Set(
          periods.filter(p => p.sessionId === session.sessionId).map(p => p.segmentId)
        );
        setSelected(previous => new Set([...previous].filter(id => !removedIds.has(id))));
      } catch (e) {
        failed.push(session);
        firstError ||= assignErrorMessage(e, 'Deletion');
      }
    }
    setPendingDeletion(failed);
    if (failed.length)
      setDeletionError(
        `${failed.length} session(s) could not be deleted. ${firstError} Retry to delete only the remaining sessions.`
      );
    setSaving(false);
  }
  const publish = onPublish
    ? async (sessionId: number, published: boolean) => {
        if (saving) return;
        setSaving(true);
        setError('');
        try {
          await onPublish(sessionId, published);
        } catch {
          setError(publishErrorMessage(published));
        } finally {
          setSaving(false);
        }
      }
    : undefined;
  // Per periods, not per render: a live board renders on every change and these walk every period.
  const ids = useMemo(
    () =>
      periods
        .map(p => p.segmentId)
        .sort((a, b) => a - b)
        .join(','),
    [periods]
  );
  // A new date/filter scope must not leave hidden rows selected.
  useEffect(() => {
    const visibleIds = new Set(ids.split(',').map(Number));
    setSelected(previous => {
      const next = new Set([...previous].filter(id => visibleIds.has(id)));
      // A scope change that empties the selection also drops the pending edit.
      if (next.size === 0 && previous.size > 0) {
        setPatch({});
        setError('');
      }
      return next;
    });
  }, [ids]);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ sizing, visibility }));
    } catch {
      /* Storage may be unavailable. */
    }
  }, [sizing, visibility]);
  const hasTags = useMemo(() => periods.some(p => p.labelIds?.length), [periods]);
  const hasLinkedRows = useMemo(() => periods.some(p => p.resourceId), [periods]);
  // Keep automatic widths stable when filtering or receiving live rows. Empty Tags
  // keeps its compact add-tag target; a manually chosen width still wins.
  const tagsWidth = hasTags ? 180 : tagsColumnWidth([], () => '');
  const columns = useMemo(
    () =>
      COLUMNS.map(c =>
        c.id === 'tags'
          ? hasTags
            ? { ...c, size: tagsWidth, minSize: Math.min(c.minSize, tagsWidth) }
            : {
                ...c,
                size: tagsWidth,
                minSize: tagsWidth,
                maxSize: tagsWidth,
                enableResizing: false,
              }
          : c
      ),
    [hasTags, tagsWidth]
  );
  const columnVisibility = { tags: hasTags, linked: hasLinkedRows, ...visibility, provider: true };
  const visibleColumns = columns.filter(
    column => columnVisibility[column.id as keyof typeof columnVisibility] !== false
  );
  const tableWidth = Math.max(0, availableWidth - (onAssign || onDelete ? 40 : 0));
  const resolvedSizing = agentTableSizing(visibleColumns, sizing, tableWidth);
  const table = useReactTable({
    data: EMPTY_DATA,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    state: {
      columnSizing: resolvedSizing,
      columnVisibility,
    },
    onColumnSizingChange: updater => {
      const next = typeof updater === 'function' ? updater(resolvedSizing) : updater;
      setSizing(previous =>
        agentTableResize(visibleColumns, resolvedSizing, next, previous, tableWidth)
      );
    },
  });
  // Stable: the checkboxes' selection context must not change when only the periods do.
  const toggle = useCallback((rows: AgentPeriod[], checked: boolean) => {
    setSelected(previous => {
      const next = new Set(previous);
      rows.forEach(p => (checked ? next.add(p.segmentId) : next.delete(p.segmentId)));
      // Deselecting everything abandons the pending edit; it must not leak
      // into the next selection. Failed rows stay selected, so a retry keeps it.
      if (next.size === 0) {
        setPatch({});
        setError('');
      }
      return next;
    });
  }, []);
  async function apply() {
    if (!onAssign || saving) return;
    const allowedIds = new Set(
      tags.flatMap(tag => [tag.id, ...(tag.children ?? []).map(child => child.id)])
    );
    if (patch.label_ids?.some(id => !allowedIds.has(id))) {
      setError('Choose tags available for every selected project.');
      return;
    }
    setSaving(true);
    setError('');
    const sessions = new Map<number, number[]>();
    periods
      .filter(p => selected.has(p.segmentId))
      .forEach(p => sessions.set(p.sessionId, [...(sessions.get(p.sessionId) || []), p.segmentId]));
    const failures: string[] = [];
    for (const [sessionId, segmentIds] of sessions) {
      try {
        await onAssign(sessionId, { ...patch, segment_ids: segmentIds });
        setSelected(previous => new Set([...previous].filter(id => !segmentIds.includes(id))));
      } catch (e) {
        failures.push(assignErrorMessage(e, 'Assignment'));
      }
    }
    if (failures.length)
      setError(
        `${failures.length} session update(s) failed. ${failures[0]} Successful changes were saved; retry the remaining selection.`
      );
    else setPatch({});
    setSaving(false);
  }
  return {
    table,
    setContainer,
    tags,
    onAssign,
    canSelect: !!onAssign || !!onDelete,
    requestDelete,
    publish,
    onPublish,
    pendingDeletion,
    deletionError,
    cancelDelete,
    confirmDelete,
    periods,
    selected,
    toggle,
    patch,
    setPatch,
    saving,
    error,
    apply,
    setSelected,
    visibility,
    setVisibility,
    setSizing,
  };
}
export type AgentTableWorkspaceValue = ReturnType<typeof useWorkspace>;
export function AgentTableWorkspace({
  periods,
  predictionPeriods = periods,
  onAssign,
  onDelete,
  onPublish,
  children,
  controls,
  heading,
  changeSession,
  wrapControls = false,
  collapseMobileControls = false,
  predictionFetchers = bulkPredictionFetchers,
}: {
  periods: AgentPeriod[];
  /** Selected suggestions follow the displayed rows without reordering other bulk mutations. */
  predictionPeriods?: AgentPeriod[];
  onAssign?: AssignReview;
  onDelete?: (sessionId: number) => Promise<void>;
  /** Owner only: publish a session or make it private again. */
  onPublish?: PublishSession;
  /** Refreshes a session after bulk predictions, in turn with the list's other changes. */
  changeSession?: (request: () => Promise<AgentSession>) => Promise<AgentSession>;
  children: React.ReactNode;
  controls?: React.ReactNode;
  heading?: React.ReactNode;
  /**
   * Let the toolbar take more than one line when the heading and the controls no longer fit
   * beside each other. Only for a table without selection: otherwise the bulk bar swaps into
   * the toolbar's fixed-height slot, and that slot must not change height.
   */
  wrapControls?: boolean;
  collapseMobileControls?: boolean;
  predictionFetchers?: typeof bulkPredictionFetchers;
}) {
  const [controlsExpanded, setControlsExpanded] = useState(false);
  const controlsId = useId();
  const hasLinkedWork = useSelector(getHasLinkedWorkPicker);
  const value = useWorkspace(periods, onAssign, onDelete, onPublish);
  const { table, tags, selected, patch, setPatch, saving } = value;
  const viewerId = useSelector(getUserId);
  const selection = useMemo(
    () => ({ selected, saving, toggle: value.toggle }),
    [selected, saving, value.toggle]
  );
  const predictions = useBulkProjectPredictions(
    onAssign ? changeSession : undefined,
    predictionFetchers
  );
  const selectedPeriods = useMemo(
    () => predictionPeriods.filter(p => selected.has(p.segmentId)),
    [predictionPeriods, selected]
  );
  const predictionTargets = bulkPredictionTargets(selectedPeriods);
  // Selection only reaches periods the viewer may change; on a colleague's page they are theirs.
  const selectedOwnerId = selectedPeriods[0]?.userId;
  // Clearing leaves unassigned periods: the owner's, or their admin's or team lead's to make.
  const canClearProject = selectedPeriods.every(
    period => period.userId === viewerId || period.sessionCanEdit
  );
  return (
    <WorkspaceProviders value={value} selection={selection}>
      <div
        ref={value.setContainer}
        className={classNames(
          styles.toolbarSlot,
          wrapControls && styles.wrapping,
          selected.size > 0 && styles.stickyBulk
        )}
      >
        <div
          className={classNames(
            styles.toolbar,
            collapseMobileControls && styles.collapsibleToolbar
          )}
          role="group"
          aria-label="Table controls"
          hidden={selected.size > 0}
        >
          {heading && <div className={styles.heading}>{heading}</div>}
          {collapseMobileControls && (
            <Button
              size="small"
              className={styles.mobileMenu}
              aria-label="Agent sessions menu"
              aria-expanded={controlsExpanded}
              aria-controls={controlsId}
              onClick={() => setControlsExpanded(expanded => !expanded)}
            >
              Menu <ChevronDown aria-hidden="true" />
            </Button>
          )}
          <div
            id={controlsId}
            className={classNames(
              styles.controlsPanel,
              controlsExpanded && styles.controlsPanelExpanded
            )}
          >
            {controls}
            <div className={styles.toolbarActions}>
              <DropdownMenu
                align="end"
                sideOffset={4}
                trigger={
                  <Button size="small" priority="medium">
                    Columns <ChevronDown />
                  </Button>
                }
                items={[
                  ...COLUMNS.filter(c => c.id !== 'provider' && c.id !== 'title').map(c => ({
                    type: 'checkboxItem' as const,
                    label: c.header,
                    checked: table.getColumn(c.id)?.getIsVisible() ?? false,
                    onCheckedChange: (checked: boolean) =>
                      value.setVisibility(v => ({ ...v, [c.id]: checked })),
                  })),
                  { type: 'separator' },
                  {
                    type: 'item',
                    label: 'Reset columns',
                    onSelect: () => {
                      value.setSizing({});
                      value.setVisibility({});
                    },
                  },
                ]}
              />
            </div>
          </div>
        </div>
        {selected.size > 0 && (
          <fieldset
            disabled={saving}
            className={styles.bulk}
            aria-label="Edit selected agent periods"
          >
            <strong>
              {selected.size} {selected.size === 1 ? 'period' : 'periods'} selected
            </strong>
            <div className={styles.bulkPicker}>
              <AgentProjectPicker
                value={patch.project_id ?? null}
                placeholder={patch.project_id === null ? 'No project' : 'Project'}
                ariaLabel="Bulk project"
                appearance="button"
                ownerId={selectedOwnerId}
                onChange={async project_id =>
                  setPatch(p => ({ ...p, project_id, label_ids: undefined }))
                }
              />
            </div>
            <div className={styles.bulkPicker}>
              <TagFilterDropdown
                tags={tags}
                selected={patch.label_ids ?? []}
                onChange={label_ids => setPatch(p => ({ ...p, label_ids }))}
                batchMode
                disableSelectNone
                singleChildSelect
                disabled={!tags.length}
                renderTarget={isOpen => (
                  <Button
                    size="small"
                    priority="medium"
                    caret
                    isActive={isOpen}
                    disabled={!tags.length}
                    tooltip={
                      tags.length ? undefined : 'No tags available for the selected projects'
                    }
                    aria-label="Bulk tags"
                  >
                    <Tag01 />
                    <span className={styles.bulkLabel}>Tags</span>
                  </Button>
                )}
              />
            </div>
            <div className={styles.bulkPicker}>
              {hasLinkedWork ? (
                <TimesheetProposalLinkedWorkSelect
                  value={patch.resource_id ?? null}
                  onChange={resource_id => setPatch(p => ({ ...p, resource_id }))}
                  disabled={saving}
                  popoverFixed
                  usePortal
                  renderTarget={({ onClick, isOpen, label, selected }) => (
                    <Button
                      size="small"
                      priority="medium"
                      caret
                      isActive={isOpen}
                      disabled={saving}
                      onClick={onClick}
                      aria-label={selected ? `Bulk linked work: ${label}` : 'Bulk linked work'}
                      aria-expanded={isOpen}
                    >
                      <Link01 />
                      <span className={styles.bulkLabel}>{selected ? label : 'Linked work'}</span>
                    </Button>
                  )}
                />
              ) : (
                <Button
                  size="small"
                  priority="medium"
                  caret
                  disabled
                  tooltip="Connect a supported integration to choose linked work"
                >
                  <Link01 />
                  <span className={styles.bulkLabel}>Linked work</span>
                </Button>
              )}
            </div>
            {onAssign && changeSession && (
              <Button
                size="small"
                priority="medium"
                disabled={saving || !!predictions.progress || !predictionTargets.length}
                isLoading={!!predictions.progress}
                tooltip={
                  predictionTargets.length
                    ? `Find a project for ${predictionTargets.length} unassigned ${predictionTargets.length === 1 ? 'period' : 'periods'} using their activity summaries`
                    : 'Selected periods are assigned, already have a suggestion, or have no activity summary'
                }
                onClick={() => void predictions.start(selectedPeriods)}
              >
                <Stars01 />
                {predictions.progress
                  ? `Suggesting ${predictions.progress.done}/${predictions.progress.total}`
                  : 'Suggest projects'}
              </Button>
            )}
            <details className={styles.columns}>
              <Button asChild size="small" priority="medium" caret>
                <summary>Clear…</summary>
              </Button>
              <div className={styles.columnMenu}>
                {canClearProject && (
                  <Button size="small" onClick={() => setPatch(p => ({ ...p, project_id: null }))}>
                    Clear project
                  </Button>
                )}
                <Button size="small" onClick={() => setPatch(p => ({ ...p, label_ids: [] }))}>
                  Clear tags
                </Button>
                <Button size="small" onClick={() => setPatch(p => ({ ...p, resource_id: null }))}>
                  Clear linked work
                </Button>
              </div>
            </details>
            <Button
              size="small"
              priority="high"
              disabled={saving || !Object.keys(patch).length}
              isLoading={saving}
              onClick={() => void value.apply()}
            >
              Update selected
            </Button>
            <Button
              size="small"
              priority="medium"
              disabled={saving}
              onClick={() => {
                value.setSelected(new Set());
                setPatch({});
                predictions.clearNotice();
              }}
            >
              Cancel
            </Button>
            {value.requestDelete && (
              <Button
                size="small"
                priority="medium"
                destructive
                disabled={saving}
                aria-label="Delete selected"
                tooltip="Delete selected"
                onClick={() =>
                  value.requestDelete?.(periods.filter(p => selected.has(p.segmentId)))
                }
              >
                <Trash02 />
              </Button>
            )}
            {!!Object.keys(patch).length && (
              <span className={styles.pending} role="status">
                Will update:{' '}
                {Object.keys(patch)
                  .filter(k => k !== 'segment_ids')
                  .map(k =>
                    k === 'project_id'
                      ? patch.project_id === null
                        ? 'clear project'
                        : 'project'
                      : k === 'label_ids'
                        ? patch.label_ids?.length
                          ? 'replace tags'
                          : 'clear tags'
                        : patch.resource_id
                          ? 'linked work'
                          : 'clear linked work'
                  )
                  .join(', ')}
              </span>
            )}
          </fieldset>
        )}
      </div>
      {value.error && <p role="alert">{value.error}</p>}
      {predictions.notice && (
        <p className={styles.notice} role={predictions.notice.error ? 'alert' : 'status'}>
          {predictions.notice.text}
        </p>
      )}
      <ProjectSuggestionReceipts>{children}</ProjectSuggestionReceipts>
      <Modal
        open={value.pendingDeletion.length > 0}
        onOpenChange={open => {
          if (!open) value.cancelDelete();
        }}
        variant="minimal"
        style={{ width: 'min(90vw, 32rem)' }}
        backdrop
        closable={!saving}
        title={
          value.pendingDeletion.length === 1
            ? 'Delete session?'
            : `Delete ${value.pendingDeletion.length} sessions?`
        }
        description="This removes the entire session and all its periods from Agent Tracking, including periods outside the current date range or project filter. Future tracker refreshes will not restore it. This cannot be undone here."
      >
        <div className={styles.deleteDialog}>
          <ul className={styles.deleteNames}>
            {value.pendingDeletion.map(session => (
              <li key={session.sessionId}>{session.title}</li>
            ))}
          </ul>
          {value.deletionError && <p role="alert">{value.deletionError}</p>}
          <div className={styles.deleteButtons}>
            <Button disabled={saving} onClick={value.cancelDelete}>
              Cancel
            </Button>
            <Button
              variant="danger"
              priority="high"
              disabled={saving}
              onClick={() => void value.confirmDelete()}
            >
              {saving
                ? 'Deleting…'
                : value.pendingDeletion.length === 1
                  ? 'Delete session'
                  : 'Delete sessions'}
            </Button>
          </div>
        </div>
      </Modal>
    </WorkspaceProviders>
  );
}
/** The workspace and its selection-only context, which the row checkboxes read on their own. */
function WorkspaceProviders({
  value,
  selection,
  children,
}: {
  value: AgentTableWorkspaceValue;
  selection: AgentSelectionValue;
  children: React.ReactNode;
}) {
  return (
    <Context.Provider value={value}>
      <AgentSelectionContext.Provider value={selection}>{children}</AgentSelectionContext.Provider>
    </Context.Provider>
  );
}

export function SelectionCheckbox({ periods, label }: { periods: AgentPeriod[]; label: string }) {
  const context = useContext(AgentSelectionContext);
  if (!context) return null;
  const count = periods.filter(p => context.selected.has(p.segmentId)).length;
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={count === periods.length && count > 0}
      ref={element => {
        if (element) element.indeterminate = count > 0 && count < periods.length;
      }}
      disabled={context.saving}
      onChange={e => context.toggle(periods, e.target.checked)}
    />
  );
}
export function ColumnResizer({ id }: { id: string }) {
  const context = useAgentTableWorkspace();
  const header = context?.table.getFlatHeaders().find(h => h.id === id);
  if (!header?.column.getCanResize()) return null;
  return (
    <Table.ThResizer
      header={header}
      role="separator"
      aria-label={`Resize ${header.column.columnDef.header} column`}
      aria-orientation="vertical"
      tabIndex={0}
      title="Drag to resize. Double-click or press Home for automatic width."
      onDoubleClick={() =>
        context?.setSizing(previous => {
          const next = { ...previous };
          delete next[id];
          return next;
        })
      }
      onKeyDown={event => {
        if (event.key === 'Home') {
          event.preventDefault();
          context?.setSizing(previous => {
            const next = { ...previous };
            delete next[id];
            return next;
          });
          return;
        }
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        context?.table.setColumnSizing({
          ...context.table.getState().columnSizing,
          [id]: Math.max(
            header.column.columnDef.minSize ?? 60,
            Math.min(
              header.column.columnDef.maxSize ?? (isFlexibleAgentColumn(id) ? Infinity : 800),
              header.getSize() + (event.key === 'ArrowRight' ? 20 : -20)
            )
          ),
        });
      }}
      columnResizeMode="onChange"
      deltaOffset={context?.table.getState().columnSizingInfo.deltaOffset ?? null}
    />
  );
}
export function AgentTags({
  periods,
  onAssign,
  disabled = false,
}: {
  periods: AgentPeriod[];
  onAssign?: AssignReview;
  disabled?: boolean;
}) {
  const allTags = useSelector(getTags);
  const tags = useAgentTagOptions(periods);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const flattened = allTags.flatMap(tag => [tag, ...(tag.children || [])]);
  const ids = [...new Set(periods.flatMap(p => p.labelIds || []))];
  const names = ids.map(id => flattened.find(tag => tag.id === id)?.name ?? 'Unavailable tag');
  async function change(selectedIds: number[]) {
    if (!onAssign || saving || disabled) return;
    setSaving(true);
    setError('');
    try {
      for (const { sessionId, labelIds, segmentIds } of agentTagAssignmentUpdates(
        periods,
        selectedIds
      )) {
        await onAssign(sessionId, { label_ids: labelIds, segment_ids: segmentIds });
      }
    } catch (e) {
      setError(assignErrorMessage(e, 'Tags'));
    } finally {
      setSaving(false);
    }
  }
  if (!ids.length && !tags.length) return null;
  if (!onAssign) {
    return (
      <span className={styles.tags}>
        {names.map((name, index) => (
          <span key={ids[index]} className={styles.tag}>
            {name}
          </span>
        ))}
      </span>
    );
  }
  return (
    <div className={styles.tagPicker} data-empty-assignment={!ids.length ? true : undefined}>
      <TagFilterDropdown
        tags={tags}
        selected={ids}
        onChange={selectedIds => void change(selectedIds)}
        disabled={disabled || saving}
        batchMode
        singleChildSelect
        disableSelectNone
        fixed
        position="bottom-start"
        renderTarget={isOpen => (
          <button
            type="button"
            className={styles.tagPill}
            data-empty={!ids.length}
            disabled={disabled || saving}
            aria-label={ids.length ? `Change tags: ${names.join(', ')}` : 'Add tags'}
            aria-expanded={isOpen}
            title={names.join(', ')}
          >
            <Tag01 />
            <span>{names[0] ?? 'Tag'}</span>
            {ids.length > 1 && <small>+{ids.length - 1}</small>}
          </button>
        )}
        listFooter={
          ids.length
            ? close => (
                <button
                  type="button"
                  className={styles.removeTags}
                  disabled={disabled || saving}
                  onClick={() => {
                    close();
                    void change([]);
                  }}
                >
                  Remove tags
                </button>
              )
            : undefined
        }
      />
      {error && <span role="alert">{error}</span>}
    </div>
  );
}
export function AgentLinkedWork({
  periods,
  onAssign,
  disabled = false,
}: {
  periods: AgentPeriod[];
  onAssign?: AssignReview;
  disabled?: boolean;
}) {
  const hasLinkedWork = useSelector(getHasLinkedWorkPicker);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const ids = [...new Set(periods.map(p => p.resourceId).filter((id): id is string => !!id))];
  async function change(previousId: string | null, resourceId: string | null) {
    if (!onAssign || saving || disabled || previousId === resourceId) return;
    setSaving(true);
    setError('');
    const sessions = new Map<number, number[]>();
    // A row can contain different tickets. Change only periods represented by
    // the clicked pill, including only visible periods in date/project groups.
    periods
      .filter(p => (p.resourceId ?? null) === previousId)
      .forEach(period => {
        sessions.set(period.sessionId, [
          ...(sessions.get(period.sessionId) ?? []),
          period.segmentId,
        ]);
      });
    try {
      for (const [sessionId, segmentIds] of sessions) {
        await onAssign(sessionId, { resource_id: resourceId, segment_ids: segmentIds });
      }
    } catch (e) {
      setError(assignErrorMessage(e, 'Linked work'));
    } finally {
      setSaving(false);
    }
  }
  if (ids.length && !hasLinkedWork)
    return (
      <span title="The linked integration is not available for this workspace">
        Linked work unavailable
      </span>
    );
  return (
    <>
      {(ids.length ? ids : onAssign && hasLinkedWork ? [null] : []).map(id => (
        <TimesheetProposalLinkedWorkSelect
          key={id ?? 'unassigned'}
          value={id}
          display="pill"
          onChange={resourceId => void change(id, resourceId)}
          disabled={!onAssign || saving || disabled}
          usePortal
          popoverFixed
        />
      ))}
      {error && <span role="alert">{error}</span>}
    </>
  );
}
